import { IKeyArray } from 'types/IKeyArray';
import { IBaseEntity, INode } from './base';
import { IGroupBasicDAO, IGroupUser, IGroupUserDAO } from './group';
import { IImage, IImageDAO } from './image';
import { IModule, IModuleDAO } from './module';
import { ISessionAttempt, ISessionAttemptDAO } from './session';
import { ISystem } from './system';
import { ITagJoinDAO } from './tag';
import { IText, ITextDAO } from './text';
import {
  IOrgUserCertificate,
  IOrgUserVariable,
  IOrgUserVariableDAO,
  IOrgUserVariableOverride,
  IOrganisationUser,
  IOrganisationUserDAO,
} from './user';
import { IEmail } from './notifications';

export interface ICourseProgress extends IBaseEntity {
  courseId: number;
  certificateId: number;
  organisationUserId: number;
  groupAcessPermissionId: number;
  moduleId: number;
  actionId: number;
  courseOutId: number;
  type: AccessPermissionType;
  status: CourseProgressStatus;
  sessionAttemptId: number;
  verificationRequestId: number;
  notificationId: number;
  progress: number;
  priority: number;
  token: string;
  tokenCreated?: number;
  limit: number;
  expiryDate: number;
  startDate: number;
  canContinue?: boolean;
  courseName: string;
  lastOrganisationUserVariableId: number;
  lastGroupUserId: number;
  statsProgress: number;
  statsTime: number;
  lastEmailId: number;
  courseNodeId: number;
  entryNodeId: number;
}

export interface ICourseProgressDAO extends ICourseProgress {
  module?: IModule;
  name?: string;
  date?: string;
  organisationUser?: IOrganisationUserDAO;
  organisationUserVariables: IOrgUserVariable[];
  organisationUserVariableOverrides: IOrgUserVariableOverride[];
  organisationUserCertificates: IOrgUserCertificate[];
  lastOrganisationUserVariable: IOrgUserVariable;
  courseOrgUserVariable: IOrgUserVariable;
  lastGroupUser: IGroupUserDAO;
  lastEmail: IEmail;
  emails: IEmail[];
  sessionAttempt: ISessionAttempt;
  sessionAttempts: ISessionAttempt[];
  log: ICourseProgressLog[];

  sendEmail?: boolean;
  alreadyExists?: boolean;
  resetCourseNode?: boolean;
  inviteUrl?: string;
}

export interface ICourseProgressLog extends IBaseEntity {
  type: CourseProgressLogType;
  sessionAttempt: ISessionAttempt;
  orgUserVariable: IOrgUserVariableDAO;
  orgUserCertificate: IOrgUserCertificate;
  email: IEmail;
}

export enum CourseProgressLogType {
  SESSION_ATTEMPT = 0,
  ORG_USER_VARIABLE = 1,
  ORG_USER_CERTIFICATE = 2,
  EMAIL = 3,
}

export interface IGroupAccessPermission extends IBaseEntity {
  courseId: number;
  groupId: number;
  notificationId: number;
  groupInstanceId: number;
  moduleInstanceId: number;
  resourceInstanceId: number;
  type: AccessPermissionType;
  status: CourseProgressStatus;
}

export interface IGroupAccessPermissionDAO extends IBaseEntity {
  courseId: number;
  courseNodeId: number;
  type: AccessPermissionType;
  status: CourseProgressStatus;
}

export enum AccessPermissionType {
  ASSIGNED = 0,
  GROUP = 1,
}
export enum CourseProgressStatus {
  DELETED = -1,
  NOT_STARTED = 0,
  ACTIVE = 1,
  DORMANT = 2,
  COMPLETE = 3,
  RESTARTED = 4,
}

export interface ISystemPermission extends IBaseEntity {
  name: string;
  systemId: number;
  courseId: number;
  nodeId: number;
  imageId: number;
  textId: number;
  courseOutId: number;
  notificationId: number;
}

export interface ISystemPermissionDAO extends ISystemPermission {
  system: ISystem;
  node: INode;
  image: IImage;
  text: IText;
  groups: ISystemGroup[];
  tags: ITagJoinDAO[];
}

export interface ISystemPermissionBasicDAO extends IBaseEntity {
  image: IImageDAO;
  text: ITextDAO;
  tags: ITagJoinDAO[];
}

export interface ISystemGroup extends IBaseEntity {
  systemPermissionId: number;
  groupId: number;
}

export interface ISystemGroupDAO extends IBaseEntity {
  group: IGroupBasicDAO;
}

export interface IPermission extends IBaseEntity {
  groupId: number;
  organisationUserId: number;
  type: PermissionType;
  status: PermissionStatus;
  certificateId: number;
  courseId: number;
  moduleId: number;
  folderId: number;
  structureId: number;
  permGroupId: number;
}

export enum PermissionType {
  READ = 0,
  WRITE = 1,
  ADMIN = 2,
}

export const PermissionTypes = {
  NONE: -1,
  READ: 0,
  WRITE: 1,
  ADMIN: 2,
};

export enum CoursePermissionType {
  VIEW = 0,
  EDIT = 1,
  ADMIN = 2,
  SHARE = 3,
}

export enum PermissionStatus {
  NOTIFIED = 1,
  ACCESSED = 2,
}

export interface ICertificatePermission extends IPermission {
  certificateId: number;
}

export interface ICoursePermission extends IBaseEntity {
  courseId: number;
  groupId: number;
  organisationUserId: number;
  organisationUser: IOrganisationUser;
  type: CoursePermissionType;
}

export interface IModulePermission extends IPermission {
  moduleId: number;
}

export interface IFolderPermission extends IPermission {
  folderId: number;
}

export interface IStructurePermission extends IPermission {
  structureId: number;
}

export interface IGroupPermission extends IPermission {
  groupId: number;
}
